/**
 * Changelog Page - Timeline layout with type filters and collapsible sections
 */
import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Collapse,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  NewReleases as NewIcon,
  BugReport as BugIcon,
  Build as ImprovementIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';

// ── Types ────────────────────────────────────────────────────────────
type ChangeType = 'feature' | 'fix' | 'improvement' | 'security';

interface Change {
  type: ChangeType;
  description: string;
}

interface ChangelogEntry {
  version: string;
  date: string;
  changes: Change[];
}

// ── Change type config ───────────────────────────────────────────────
const typeConfig: Record<ChangeType, { label: string; color: string; darkColor: string; icon: React.ReactNode }> = {
  feature: { label: 'Feature', color: '#6366F1', darkColor: '#818CF8', icon: <NewIcon sx={{ fontSize: 16 }} /> },
  fix: { label: 'Fix', color: '#EF4444', darkColor: '#F87171', icon: <BugIcon sx={{ fontSize: 16 }} /> },
  improvement: { label: 'Improvement', color: '#3B82F6', darkColor: '#60A5FA', icon: <ImprovementIcon sx={{ fontSize: 16 }} /> },
  security: { label: 'Security', color: '#F59E0B', darkColor: '#FBBF24', icon: <SecurityIcon sx={{ fontSize: 16 }} /> },
};

// ── Changelog data ───────────────────────────────────────────────────
const changelog: ChangelogEntry[] = [
  {
    version: '1.22.0',
    date: '2026-07-14',
    changes: [
      { type: 'feature', description: 'Spec-Driven Development (OpenSpec): new opt-in per-team mode where the agent writes a formal OpenSpec change, validates it with the openspec CLI, and has a second AI session review it before implementing — skips the plan-approval step entirely when the spec is accepted' },
      { type: 'feature', description: 'Configurable Spec Review Attempts: new "Max Spec Review Attempts" field (1-10, default 2) in Team Agent settings — falls back to the normal human plan-review flow if the spec can\'t converge in time, correctly carrying the rejection feedback into the next attempt' },
      { type: 'feature', description: 'Keyword-Based Repo Auto-Matching: !agent <task> now matches a repository by name/keyword overlap with the task description, not just an explicit URL — a single unambiguous match skips repo confirmation entirely, and tied matches show only the tied candidates' },
      { type: 'improvement', description: 'Live Default-Branch Resolution: agent runner now resolves a repository\'s live GitHub default branch instead of trusting a potentially stale cached value, self-healing the cache when it drifts' },
      { type: 'fix', description: 'Stale base_branch Auto-Fill: confirming a repository no longer silently caches a stale default branch into the task unless the user explicitly specified one' },
      { type: 'security', description: 'Redacted a GitHub PAT that could leak into agent-runner logs if a default-branch lookup timed out, pinned the OpenSpec CLI version, and bumped vulnerable frontend/backend dependencies flagged by Dependabot' },
      { type: 'improvement', description: 'CI: agent-runner test suite now runs in CI (not just linting), and GitHub Actions bumped to Node24-native versions ahead of the Node20 deprecation' },
    ],
  },
  {
    version: '1.21.0',
    date: '2026-03-10',
    changes: [
      { type: 'feature', description: 'GitHub OAuth Login: Users can now sign in with their GitHub account, restricted to a configured organization' },
      { type: 'feature', description: 'MCP Connection Health Checks: Three-state indicators (connected / partial / disconnected) for JIRA, Confluence, and GitHub with a "Test Connection" button for on-demand checks' },
      { type: 'feature', description: 'Personal Access Token Auth: MCP integrations now use PATs instead of basic auth, bypassing SSO-protected Atlassian instances' },
      { type: 'feature', description: 'Confluence via VPN Gateway: Confluence traffic now routes through the shared VPN gateway (nginx SNI proxy) alongside JIRA — single VPN connection serves both services' },
      { type: 'improvement', description: 'MCP health checks run per-team using each team\'s own tokens from the database instead of global config' },
      { type: 'improvement', description: 'Simplified Confluence setup: removed username field — PAT-only authentication requires just a base URL and token' },
      { type: 'fix', description: 'GHCR pull secret now auto-refreshed during deploys, preventing image pull failures after token rotation' },
      { type: 'fix', description: 'Production deploys now force pod replacement via deployCommit annotation, ensuring latest images are always pulled' },
    ],
  },
  {
    version: '1.20.0',
    date: '2026-03-03',
    changes: [
      { type: 'feature', description: 'Per-Team Timeout Configuration: Question, planning, and implementation timeouts are now configurable per team from the Agent tab in Team Settings' },
      { type: 'feature', description: 'Per-Team Task Queue Limit: Max concurrent tasks setting is now enforced per team by the agent runner (previously global only)' },
      { type: 'feature', description: '!github link Command: New command to link a GitHub repository to a team directly from Google Chat' },
      { type: 'improvement', description: 'Question timeout default increased from 300s to 600s to reduce timeouts on complex questions' },
      { type: 'improvement', description: 'Timeout values stored in database — changes take effect immediately without redeployment' },
    ],
  },
  {
    version: '1.19.0',
    date: '2026-02-26',
    changes: [
      { type: 'feature', description: 'Parallel Task Processing: Agent runner now processes up to 3 tasks concurrently using asyncio worker pool instead of sequential execution' },
      { type: 'feature', description: 'Task Types — !agent ask: New question/review mode that skips planning and git workflow, answers directly in chat with shorter timeout' },
      { type: 'feature', description: 'Smart No-Changes Detection: Agent classifies "no file changes" as completed (not needed) or failed (blocked) with a user-facing explanation instead of a blanket failure' },
      { type: 'feature', description: 'Repo Confirmation Flow: Auto-detected repositories now ask for user verification before proceeding, with !agent yes shortcut for quick confirmation' },
      { type: 'feature', description: 'Stale Task Recovery: Tasks stuck in planning/implementing/answering for over 1 hour are automatically marked as failed and recovered' },
      { type: 'feature', description: 'Task Completion Summary: Completion notifications now include a plan summary of what was done, alongside branch and PR links' },
      { type: 'improvement', description: 'Session timeout increased from 600s to 2400s (40 min) to support large repository implementations' },
      { type: 'improvement', description: 'Max agent turns increased from 50 to 100 for more complex tasks' },
      { type: 'improvement', description: 'Session log loading indicator: Animated progress bar replaces static "Waiting for output" text when connecting to agent sessions' },
      { type: 'fix', description: 'Tasks stuck in planning: Added exception handlers to planning and implementation phases — previously unhandled errors left tasks in active state forever' },
      { type: 'fix', description: 'Agent help text reorganized by category with all new commands documented' },
    ],
  },
  {
    version: '1.18.0',
    date: '2026-02-24',
    changes: [
      { type: 'feature', description: 'Agentic AI Integration: New autonomous AI agent (Claude Code) that can plan and implement tasks end-to-end from Google Chat commands (!agent)' },
      { type: 'feature', description: 'Agent Runner Service: Standalone container that clones repos, creates branches, runs Claude Code CLI, commits changes, creates PRs, and posts updates to Google Chat' },
      { type: 'feature', description: 'Model Context Protocol (MCP): Agent sessions configured with MCP servers for direct access to GitHub, JIRA, and Confluence APIs' },
      { type: 'feature', description: 'Agent Configuration UI: New "Agent" tab in Team Hub with runner health indicator, persona/instructions config, task queue, and usage statistics' },
      { type: 'feature', description: 'Agent Feature Flag: New "Enable Agent" toggle in Team Settings to activate/deactivate the agent per team' },
      { type: 'feature', description: 'Agent Health Monitoring: End-to-end health check from runner heartbeat, backend API, and frontend UI indicator with last heartbeat timestamp' },
      { type: 'feature', description: 'Agent Chat Commands: !agent <task>, !agent queue, !agent approve/reject/cancel <task_id>, !agent help' },
      { type: 'feature', description: 'Helm Deployment: New agent-runner-deployment.yaml with K8s liveness/readiness probes and configurable resources' },
      { type: 'feature', description: 'Docker Compose: Agent runner available via docker compose --profile agent up' },
      { type: 'improvement', description: 'New agent_config and agent_tasks database tables with full task lifecycle tracking (pending, planning, review, in_progress, completed/failed)' },
    ],
  },
  {
    version: '1.17.0',
    date: '2026-02-19',
    changes: [
      { type: 'feature', description: 'Row Deletion in Team Detail Page: All five data tables (Members, Documentation, Repositories, Experts, Responsibility Matrix) now have per-row delete buttons with confirmation and snackbar feedback' },
      { type: 'feature', description: 'Repository Delete API: New DELETE endpoint for repositories — previously missing while other entities already had one' },
      { type: 'fix', description: 'AI Asking for Experts: Bot no longer asks "which team or expert to involve" when no expert matches — team prompts updated to request technical details instead' },
    ],
  },
  {
    version: '1.16.0',
    date: '2026-02-18',
    changes: [
      { type: 'feature', description: 'Live Application Logs in Admin Portal: Real-time log viewer with in-memory ring buffer, 48-hour hourly activity chart, live polling, level/logger/search filters, and expandable stack traces' },
      { type: 'fix', description: 'AI Inventing Expert Names: Fixed hallucinated expert names (e.g. "Jordan Liu") when no expert matched — strip logic now always runs regardless of expert match' },
      { type: 'fix', description: 'Logging Configuration: LOG_LEVEL env var was not consumed — root logger defaulted to WARNING, hiding INFO/DEBUG logs' },
      { type: 'fix', description: 'Google Chat Bold Formatting: Auto-appended expert sections used ** (Markdown) instead of * (Google Chat bold)' },
      { type: 'fix', description: 'NOTICE Section Matching: Expert/team lead sections now correctly insert before NOTICE regardless of * vs ** formatting' },
    ],
  },
  {
    version: '1.15.0',
    date: '2026-02-17',
    changes: [
      { type: 'feature', description: 'Author Resolution via People API: Newsletter topics now resolve author names and emails using Google People API instead of incomplete Chat API sender data' },
      { type: 'feature', description: 'Clickable Google Chat Mentions: Expert and team lead mentions are now proper <users/ID> mentions that trigger notifications — resolved by email via People API directory search' },
      { type: 'feature', description: 'Newsletter Message Deduplication: Time-based deduplication prevents re-processing of messages across polling cycles' },
      { type: 'feature', description: 'Google OAuth Scopes: Added chat.memberships.readonly and directory.readonly for member lookup and People API access' },
      { type: 'feature', description: 'Team-Level AI Provider: Teams can now configure their own AI provider (including Claude) with per-team API keys and endpoints' },
      { type: 'fix', description: 'Unknown Newsletter Author: Authors no longer show as "Unknown" — resolved via Google People API fallback chain' },
      { type: 'fix', description: 'Expert Tagging Not Clickable: Mentions are now proper clickable tags instead of plain text @tag' },
      { type: 'fix', description: 'AI-Generated Fake Expert Mentions: AI no longer generates its own expert section — auto-append handles it with real clickable mentions' },
      { type: 'fix', description: 'Newsletter "No" Overrides Summary: Saying "no" to links no longer overwrites the confirmed summary' },
      { type: 'fix', description: 'Confluence SSO Login Page: Bot no longer summarizes the SSO login page — proper auth fallback and login page detection added' },
      { type: 'fix', description: 'Team AI Model Override: Team-level model_override now correctly takes precedence' },
      { type: 'fix', description: 'Stale Team AI Config: Fixed stale team config overriding global IONOS token' },
      { type: 'fix', description: 'AI Provider Helm Wiring: AI provider env vars now correctly wired through Helm charts and deploy workflow' },
      { type: 'improvement', description: 'Newsletter Topics UI shows author email as fallback when author name is "Unknown"' },
    ],
  },
  {
    version: '1.14.0',
    date: '2026-02-12',
    changes: [
      { type: 'feature', description: 'GitHub PR Support: Bot now recognizes and fetches context for GitHub Pull Request URLs — displays title, state, author, branches, changed files, and reviews' },
      { type: 'feature', description: 'Deep Repository Code Search: New targeted search across source code, changelogs, and release history to answer questions about features, implementations, and troubleshooting' },
      { type: 'feature', description: 'Smart Repo Search Detection: Bot automatically detects when a question benefits from deep code search (feature checks, troubleshooting, implementation details, version queries)' },
      { type: 'fix', description: 'Confluence SSO Authentication: SSO session cookies are now loaded from the database at startup — no longer lost on app restart' },
      { type: 'fix', description: 'Confluence SSO Authentication: Bot no longer requires basic auth credentials when SSO cookies are configured' },
      { type: 'fix', description: 'Newsletter Confirmation Logic: Feedback like "partially correct" or "please include" is no longer mistaken for confirmation' },
      { type: 'fix', description: 'Newsletter Bystander Messages: Only the original author can advance the conversation state' },
      { type: 'fix', description: 'Newsletter Accidental Submission: Final confirmation now requires strict short responses (5 words or fewer) to prevent accidental saves' },
      { type: 'fix', description: 'Newsletter Summary Refinement: Subsequent refinements now build on the current summary instead of restarting from the original message' },
      { type: 'fix', description: 'Newsletter Date Filtering: Fixed date filtering in admin UI returning no results — dates are now timezone-aware and to_date covers end-of-day' },
      { type: 'improvement', description: 'Repository search coverage increased: more files scanned in tree browse (5 to 8) and more results returned (3 to 5 code matches)' },
      { type: 'improvement', description: 'Repository search breadth: searches up to 3 repos (from 2) when deep code search is triggered' },
    ],
  },
  {
    version: '1.13.0',
    date: '2025-02-10',
    changes: [
      { type: 'feature', description: 'Added Newsletter Topics Collection feature - bot monitors a configured Google Chat space for newsletter submissions' },
      { type: 'feature', description: 'Multi-turn conversation flow for newsletter topics: AI generates summary and category, asks for confirmation, collects links, and saves to database' },
      { type: 'feature', description: 'AI-powered categorization into Success Stories, Recognition, Open Source, Industry Pulse, or Other' },
      { type: 'feature', description: 'Admin Newsletter Topics page with filtering by date range, category, and author' },
      { type: 'feature', description: 'Newsletter topics export to markdown format grouped by category' },
      { type: 'feature', description: 'Newsletter configuration in Admin UI - set space ID and enable/disable polling without redeploying' },
      { type: 'feature', description: 'Database-first configuration with environment variable fallback for newsletter settings' },
      { type: 'improvement', description: 'Added newsletter_topics and newsletter_conversations tables to analytics schema' },
      { type: 'improvement', description: 'Automatic cleanup of expired newsletter conversations after 24 hours' },
    ],
  },
  {
    version: '1.12.0',
    date: '2025-01-19',
    changes: [
      { type: 'feature', description: '!jira import-github bulk import now requires explicit "bulk" keyword for safety (e.g., !jira import-github bulk <repo-url> Task)' },
      { type: 'fix', description: 'Fixed JIRA bulk ticket creation failing with "Priority Medium is not valid" - now uses JIRA default priority' },
      { type: 'fix', description: 'Fixed Confluence integration not working - CONFLUENCE_API_TOKEN was not being injected into backend deployment' },
      { type: 'fix', description: 'Fixed Confluence URLs in user messages not using authenticated ConfluenceClient - now properly fetches internal Confluence pages' },
      { type: 'fix', description: 'Fixed Confluence base URL missing https:// protocol in Helm values' },
      { type: 'fix', description: 'Fixed Confluence URL parsing to handle /spaces/SPACE/pages/ID format (without /wiki prefix)' },
      { type: 'fix', description: 'Fixed Confluence API endpoint for on-premise instances - removed hardcoded /wiki prefix from REST API calls' },
    ],
  },
  {
    version: '1.11.0',
    date: '2025-01-16',
    changes: [
      { type: 'feature', description: '!jira import-github now supports importing a single issue by URL' },
      { type: 'feature', description: '!jira my tickets now accepts an optional email parameter to view tickets for a specific user' },
      { type: 'fix', description: 'Fixed !jira link argument parsing - both issue keys are now properly detected' },
      { type: 'fix', description: 'Fixed !jira stats showing all zeros by fetching all tickets and categorizing locally' },
      { type: 'fix', description: 'Fixed !jira import-github "github_token_encrypted" attribute error with hasattr() fallback' },
      { type: 'fix', description: 'Fixed !jira my tickets user lookup - now tries multiple JQL approaches for different JIRA configurations' },
      { type: 'improvement', description: 'Added comprehensive logging to GitHub API calls for better debugging' },
      { type: 'improvement', description: 'Added logging to JIRA client methods (user tickets, epic children, stale, overdue, stats)' },
      { type: 'improvement', description: '!jira epic now tries 4 different JQL approaches to handle different JIRA configurations' },
      { type: 'improvement', description: 'Better error messages for !jira import-github with troubleshooting tips' },
    ],
  },
  {
    version: '1.10.0',
    date: '2025-01-16',
    changes: [
      { type: 'feature', description: '!jira create now supports issue types: task, story, bug, epic, initiative' },
      { type: 'feature', description: '!jira create without title generates content from conversation using AI' },
      { type: 'feature', description: 'Issue-type specific templates for JIRA tickets (Task, Story, Bug, Epic, Initiative)' },
      { type: 'feature', description: 'AI fills template fields based on conversation context (e.g., Bug template gets Steps to Reproduce, Expected/Actual Behavior)' },
      { type: 'improvement', description: 'Quick create tickets include structured templates with placeholder text for easy editing' },
      { type: 'fix', description: 'Fixed !jira commands returning "Permission Denied" when using message polling (worker.py routing fix)' },
      { type: 'fix', description: 'Fixed JIRA API error handling to show detailed error messages instead of generic 400 errors' },
      { type: 'fix', description: 'Removed hardcoded "Medium" priority that was invalid for some JIRA instances' },
    ],
  },
  {
    version: '1.9.0',
    date: '2025-01-15',
    changes: [
      { type: 'feature', description: 'Added !jira commands for ticket management - create tickets, search, view stale/overdue tickets, and get project stats' },
      { type: 'feature', description: 'Added Enable JIRA Commands toggle in Feature Settings (disabled by default)' },
      { type: 'feature', description: 'Smart ticket creation from thread context - AI generates title and description from conversation' },
      { type: 'feature', description: 'Natural language search for JIRA tickets (e.g., "open bugs about authentication")' },
      { type: 'feature', description: 'Quick filters: !jira filter open, in-progress, blocked, high-priority' },
      { type: 'feature', description: '!jira filter save/list/delete - Save and reuse custom JQL filters' },
      { type: 'feature', description: '!jira bulk - create multiple tickets at once with type support' },
      { type: 'feature', description: '!jira link - link issues to epics or epics to initiatives' },
      { type: 'feature', description: '!jira epic - list all tickets under an epic' },
      { type: 'feature', description: '!jira initiatives - show full project hierarchy with indentation' },
      { type: 'feature', description: '!jira import-github - migrate GitHub repository issues to JIRA project' },
      { type: 'feature', description: '!jira stale - shows tickets not updated in 7+ days' },
      { type: 'feature', description: '!jira overdue - shows tickets past their due date' },
      { type: 'feature', description: '!jira stats - shows project statistics by status and priority' },
      { type: 'improvement', description: 'JIRA permission handling - bot informs users if service account lacks project access' },
    ],
  },
  {
    version: '1.8.0',
    date: '2025-01-15',
    changes: [
      { type: 'feature', description: 'Added AI Context Templates with full CRUD support - create example conversations to train the AI' },
      { type: 'feature', description: 'AI now receives full team context: documentation, repositories, team competency matrix, and teams matrix' },
      { type: 'feature', description: 'Added !reset command to clear cache/context for a thread' },
      { type: 'feature', description: 'Added !bad command for SMEs to flag bot responses as incorrect - flagged entries appear in orange on Cache Review page' },
      { type: 'feature', description: 'Added Google Chat user tagging support for clickable @mentions in bot responses' },
      { type: 'feature', description: 'Added Google Chat User ID field to Team Competency and Team Lead configuration' },
      { type: 'feature', description: 'Added Web URL Fetching - bot can now fetch and read content from URLs in user messages' },
      { type: 'feature', description: 'Added Proactive Documentation Fetching - bot automatically searches configured Confluence pages, GitHub repos, and documentation' },
      { type: 'improvement', description: 'Renamed "Experts" tab to "Team Competency" and "Responsibilities" to "Teams Matrix"' },
      { type: 'improvement', description: 'Renamed "Prompts" tab to "AI Context Templates" with inline create/edit/delete functionality' },
      { type: 'improvement', description: 'Added pagination to Cache Review page for better performance with large datasets' },
      { type: 'improvement', description: 'Added "Flagged Bad" stats card to Cache Review page' },
      { type: 'fix', description: 'Context templates now add to system prompt instead of replacing it (few-shot learning)' },
      { type: 'fix', description: 'Fixed Dashboard "Queries Today" widget not displaying data correctly' },
    ],
  },
  {
    version: '1.7.0',
    date: '2025-01-15',
    changes: [
      { type: 'feature', description: 'Added AI model tier classification (Standard, Plus, Premium) with color-coded badges' },
      { type: 'feature', description: 'Changed AI model selection to dropdown with all available IONOS models' },
      { type: 'feature', description: 'Added "Disable Auto Expert Append" toggle to prevent duplicate expert assignments' },
      { type: 'fix', description: 'Fixed AI models API endpoint path returning 404' },
      { type: 'fix', description: 'Fixed UUID serialization error when fetching available models' },
      { type: 'fix', description: 'Fixed duplicate expert assignment when system prompt uses different formatting' },
    ],
  },
  {
    version: '1.6.0',
    date: '2025-01-14',
    changes: [
      { type: 'fix', description: 'Fixed bug where system prompts configured in admin portal were not applied to bot responses' },
      { type: 'feature', description: 'Added Response Caching toggle in Feature Settings to enable/disable cached responses' },
      { type: 'improvement', description: 'Response caching now disabled by default - teams can enable when cached answers are reliable' },
    ],
  },
  {
    version: '1.5.0',
    date: '2025-01-12',
    changes: [
      { type: 'feature', description: 'Added invitation code registration system for enhanced security' },
      { type: 'feature', description: 'New Invite Codes tab in Users & Permissions for admin code management' },
      { type: 'feature', description: 'Admins can generate codes with optional email hints and expiration dates' },
      { type: 'improvement', description: 'Registration now validates invitation codes before account creation' },
    ],
  },
  {
    version: '1.4.0',
    date: '2024-12-16',
    changes: [
      { type: 'feature', description: 'Restructured admin UI with tabbed interfaces for better organization' },
      { type: 'feature', description: 'Added Command Permissions tab showing all bot commands with descriptions' },
      { type: 'improvement', description: 'Combined Team Management and Approvals into Teams & Approvals page' },
      { type: 'improvement', description: 'Combined User Management with Command Permissions into Users & Permissions page' },
      { type: 'improvement', description: 'Renamed Feedback Management to Users Feedback' },
    ],
  },
  {
    version: '1.3.0',
    date: '2024-12-16',
    changes: [
      { type: 'feature', description: 'Added Multiple Thread Responses toggle in Feature Settings' },
      { type: 'feature', description: 'Added !ask command for follow-up questions when multiple responses is disabled' },
      { type: 'feature', description: 'Added Alembic database migration system for automated schema updates' },
      { type: 'improvement', description: 'Default behavior now responds once per thread (use !ask for follow-ups)' },
      { type: 'improvement', description: 'Database migrations now run automatically on deployment' },
    ],
  },
  {
    version: '1.2.0',
    date: '2024-12-10',
    changes: [
      { type: 'feature', description: 'Added version display in the UI footer' },
      { type: 'feature', description: 'Added Changelog page with release notes' },
      { type: 'fix', description: 'Team leads now only see teams they belong to, not all teams' },
      { type: 'fix', description: 'Fixed 500 error on /api/v1/activity/recent endpoint' },
      { type: 'fix', description: 'Fixed 500 error on /api/v1/teams/{id}/ai-config endpoint' },
      { type: 'improvement', description: 'Deploy workflow now forces pod restart to pull latest images' },
      { type: 'improvement', description: 'Enhanced default response template with icons and better formatting for Google Chat' },
    ],
  },
  {
    version: '1.1.0',
    date: '2025-01-10',
    changes: [
      { type: 'feature', description: 'Added user feedback and feature request system' },
      { type: 'feature', description: 'Added admin feedback management page' },
      { type: 'feature', description: 'Added documentation page with guides and best practices' },
      { type: 'improvement', description: 'Improved team onboarding wizard flow' },
      { type: 'improvement', description: 'Better error handling across the application' },
    ],
  },
  {
    version: '1.0.0',
    date: '2025-01-01',
    changes: [
      { type: 'feature', description: 'Initial release of AI Wingman' },
      { type: 'feature', description: 'Team creation and management' },
      { type: 'feature', description: 'AI-powered response caching system' },
      { type: 'feature', description: 'Analytics dashboard with usage metrics' },
      { type: 'feature', description: 'Admin approval workflow for teams' },
      { type: 'security', description: 'JWT authentication with Google OAuth support' },
    ],
  },
];

// ── Helpers ──────────────────────────────────────────────────────────
const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const INITIALLY_EXPANDED = 5; // Latest N versions expanded by default

// ── Component ────────────────────────────────────────────────────────
export default function ChangelogPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [filter, setFilter] = useState<ChangeType | 'all'>('all');
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    changelog.forEach((entry, i) => {
      initial[entry.version] = i < INITIALLY_EXPANDED;
    });
    return initial;
  });

  const toggleExpand = (version: string) => {
    setExpanded((prev) => ({ ...prev, [version]: !prev[version] }));
  };

  const expandAll = () => {
    const next: Record<string, boolean> = {};
    changelog.forEach((e) => { next[e.version] = true; });
    setExpanded(next);
  };

  const collapseAll = () => {
    const next: Record<string, boolean> = {};
    changelog.forEach((e) => { next[e.version] = false; });
    setExpanded(next);
  };

  // Summary stats
  const stats = useMemo(() => {
    const counts = { feature: 0, fix: 0, improvement: 0, security: 0 };
    changelog.forEach((e) => e.changes.forEach((c) => counts[c.type]++));
    return counts;
  }, []);

  // Filter changes per release
  const getFilteredChanges = (changes: Change[]) =>
    filter === 'all' ? changes : changes.filter((c) => c.type === filter);

  // Timeline colors
  const timelineLine = isDark ? 'rgba(255,255,255,0.08)' : '#E5E7EB';
  const dotBorder = isDark ? '#1A1D2E' : '#FFFFFF';

  const getTypeColor = (type: ChangeType) =>
    isDark ? typeConfig[type].darkColor : typeConfig[type].color;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Changelog
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Track every update, bug fix, and new feature across releases
      </Typography>

      {/* Stats bar */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          {changelog.length} releases
        </Typography>
        {(Object.keys(typeConfig) as ChangeType[]).map((type) => (
          <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: getTypeColor(type),
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {stats[type]} {typeConfig[type].label.toLowerCase()}s
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Filter chips + expand/collapse */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          mb: 4,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <FilterIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 0.5 }} />
        <Chip
          label="All"
          size="small"
          variant={filter === 'all' ? 'filled' : 'outlined'}
          onClick={() => setFilter('all')}
          sx={{
            fontWeight: 500,
            ...(filter === 'all' && {
              backgroundColor: isDark ? 'rgba(255,255,255,0.12)' : '#F3F4F6',
              color: 'text.primary',
            }),
          }}
        />
        {(Object.keys(typeConfig) as ChangeType[]).map((type) => (
          <Chip
            key={type}
            label={typeConfig[type].label}
            size="small"
            variant={filter === type ? 'filled' : 'outlined'}
            onClick={() => setFilter(filter === type ? 'all' : type)}
            sx={{
              fontWeight: 500,
              ...(filter === type && {
                backgroundColor: getTypeColor(type),
                color: '#FFFFFF',
                '&:hover': { backgroundColor: getTypeColor(type) },
              }),
            }}
          />
        ))}

        <Box sx={{ flex: 1 }} />
        <Typography
          variant="caption"
          sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
          onClick={expandAll}
        >
          Expand all
        </Typography>
        <Typography variant="caption" color="text.secondary">|</Typography>
        <Typography
          variant="caption"
          sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
          onClick={collapseAll}
        >
          Collapse all
        </Typography>
      </Box>

      {/* Timeline */}
      <Box sx={{ position: 'relative', pl: { xs: 3, sm: 4 } }}>
        {/* Vertical line */}
        <Box
          sx={{
            position: 'absolute',
            left: { xs: 11, sm: 15 },
            top: 0,
            bottom: 0,
            width: 2,
            backgroundColor: timelineLine,
          }}
        />

        {changelog.map((release, index) => {
          const filteredChanges = getFilteredChanges(release.changes);
          const isExpanded = expanded[release.version] ?? false;
          const isLatest = index === 0;

          // Skip releases with no matching changes when filtering
          if (filter !== 'all' && filteredChanges.length === 0) return null;

          return (
            <Box key={release.version} sx={{ position: 'relative', mb: 4 }}>
              {/* Timeline dot */}
              <Box
                sx={{
                  position: 'absolute',
                  left: { xs: -21, sm: -17 },
                  top: 4,
                  width: isLatest ? 16 : 12,
                  height: isLatest ? 16 : 12,
                  borderRadius: '50%',
                  backgroundColor: isLatest
                    ? (isDark ? '#818CF8' : '#6366F1')
                    : (isDark ? 'rgba(255,255,255,0.2)' : '#D1D5DB'),
                  border: `3px solid ${dotBorder}`,
                  zIndex: 1,
                  ...(isLatest && {
                    boxShadow: isDark
                      ? '0 0 0 4px rgba(129, 140, 248, 0.2)'
                      : '0 0 0 4px rgba(99, 102, 241, 0.15)',
                  }),
                }}
              />

              {/* Version header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.85 },
                }}
                onClick={() => toggleExpand(release.version)}
              >
                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  v{release.version}
                </Typography>
                {isLatest && (
                  <Chip
                    label="Latest"
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      backgroundColor: isDark ? 'rgba(52, 211, 153, 0.15)' : '#D1FAE5',
                      color: isDark ? '#34D399' : '#059669',
                    }}
                  />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  {formatDate(release.date)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  &middot; {release.changes.length} change{release.changes.length !== 1 ? 's' : ''}
                </Typography>

                {/* Change type summary dots */}
                <Box sx={{ display: 'flex', gap: 0.5, ml: 0.5 }}>
                  {(Object.keys(typeConfig) as ChangeType[]).map((type) => {
                    const count = release.changes.filter((c) => c.type === type).length;
                    if (count === 0) return null;
                    return (
                      <Box
                        key={type}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: getTypeColor(type),
                          opacity: 0.7,
                        }}
                        title={`${count} ${typeConfig[type].label.toLowerCase()}${count > 1 ? 's' : ''}`}
                      />
                    );
                  })}
                </Box>

                <Box sx={{ flex: 1 }} />
                <IconButton
                  size="small"
                  sx={{
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Changes list */}
              <Collapse in={isExpanded} timeout={200}>
                <Box sx={{ mt: 1.5, ml: 0.5 }}>
                  {filteredChanges.map((change, ci) => (
                    <Box
                      key={ci}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        py: 0.75,
                        '&:not(:last-child)': {
                          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : '#F3F4F6'}`,
                        },
                      }}
                    >
                      {/* Type badge */}
                      <Chip
                        icon={typeConfig[change.type].icon as React.ReactElement}
                        label={typeConfig[change.type].label}
                        size="small"
                        sx={{
                          minWidth: 110,
                          height: 24,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          backgroundColor: `${getTypeColor(change.type)}15`,
                          color: getTypeColor(change.type),
                          borderRadius: 1.5,
                          '& .MuiChip-icon': {
                            color: getTypeColor(change.type),
                            ml: 0.5,
                          },
                          flexShrink: 0,
                          mt: 0.25,
                        }}
                      />
                      {/* Description */}
                      <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.primary' }}>
                        {change.description}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
